import { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Divider,
	TextField,
	Unstable_Grid2 as Grid
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { CREATE_SHOP, UPDATE_SHOP } from '../../gql/queries';

interface ShopFormProps {
	data?: {
		_id?: string;
		name?: string;
	};
	onClose: () => void;
	refetch: () => void;
}

export default function EditShop({ data, onClose, refetch }: ShopFormProps) {
	const [formData, setFormData] = useState({
		name: data?.name || ''
	});

	const [createShop, { loading: creating }] = useMutation(CREATE_SHOP);
	const [updateShop, { loading: updating }] = useMutation(UPDATE_SHOP);

	const isEdit = !!data?._id;
	const loading = creating || updating;

	useEffect(() => {
		if (data) {
			setFormData({
				name: data.name || ''
			});
		}
	}, [data]);

	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
	}, []);

	const handleSubmit = useCallback(async (event: React.FormEvent) => {
		event.preventDefault();

		// Validation
		if (!formData.name.trim()) {
			toast.error('Shop name is required');
			return;
		}

		try {
			if (isEdit) {
				// Update existing shop
				await updateShop({
					variables: {
						shopId: data._id,
						shop: {
							name: formData.name.trim(),
							location: [] // Required by input type but not used for update
						}
					}
				});
				toast.success('Shop updated successfully');
			} else {
				// Create new shop
				await createShop({
					variables: {
						shop: {
							name: formData.name.trim(),
							location: [] // Start with empty locations
						}
					}
				});
				toast.success('Shop created successfully');
			}

			refetch();
			onClose();
		} catch (error: any) {
			toast.error(`Failed to ${isEdit ? 'update' : 'create'} shop: ${error.message}`);
		}
	}, [formData, isEdit, data, createShop, updateShop, refetch, onClose]);

	return (
		<form onSubmit={handleSubmit}>
			<Card>
				<CardHeader
					title={isEdit ? 'Edit Shop' : 'Create New Shop'}
					subheader={isEdit ? 'Update shop details' : 'Add a new shop to the system'}
				/>
				<Divider />
				<CardContent>
					<Grid container spacing={3}>
						<Grid xs={12}>
							<TextField
								fullWidth
								label="Shop Name"
								name="name"
								onChange={handleChange}
								required
								value={formData.name}
								helperText="Enter a unique name for the shop"
								disabled={loading}
							/>
						</Grid>
					</Grid>
				</CardContent>
				<Divider />
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 2,
						p: 2
					}}
				>
					<Button
						color="inherit"
						onClick={onClose}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button
						color="primary"
						type="submit"
						variant="contained"
						disabled={loading}
					>
						{loading ? 'Saving...' : isEdit ? 'Update Shop' : 'Create Shop'}
					</Button>
				</Box>
			</Card>
		</form>
	);
}
